import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Spin, Alert, Button, Space, Tag, Row, Col } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, QrcodeOutlined } from '@ant-design/icons';
import QRCode from 'qrcode';
import { eventApi } from '../services/eventApi';
import { API_CONFIG } from '../config/apiConfig';

const { Title, Text } = Typography;

interface EventSession {
  id: string;
  eventName: string;
  location: string;
  startTime: string;
  endTime: string;
  status: 'active' | 'inactive' | 'ended';
  qrRefreshInterval: number;
  description?: string;
  createdAt: string;
}

const QRDisplayPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [currentToken, setCurrentToken] = useState<string>('');
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  // Fetch event details
  const fetchEvent = async () => {
    if (!eventId) return;
    
    try {
      setLoading(true);
      const response = await eventApi.getEventById(eventId);
      if (response.data.success) {
        setEvent(response.data.data);
        setError(null);
      } else {
        setError('Failed to load event');
      }
    } catch (err: any) {
      console.error('Error fetching event:', err);
      setError(err.response?.data?.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  // Generate QR token and QR code
  const generateQRCode = async () => {
    if (!eventId) return;

    try {
      // Use eventId as sessionId (they are the same in this context)
      const tokenResponse = await eventApi.generateQRToken(eventId);
      console.log('Token response:', tokenResponse); // Debug log
      
      // Extract token from the response structure
      const tokenData = tokenResponse.data.data;
      console.log('Extracted token data:', tokenData); // Debug log
      
      // Get the token string from response
      const token = tokenData.token;
      
      if (!token || typeof token !== 'string') {
        throw new Error('Token is not a valid string');
      }
      setCurrentToken(token);

      // Create the QR data as a full URL to the game client
      // Use configured client base URL
      const qrData = `${API_CONFIG.clientBaseURL}/join-event/${token}`;
      
      console.log('Generated QR URL:', qrData); // Debug log
      
      // Generate QR code with the full URL
      const qrDataUrl = await QRCode.toDataURL(qrData, {
        width: 400,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      setQrCodeUrl(qrDataUrl);
      
      // Reset timer
      if (event) {
        setTimeRemaining(event.qrRefreshInterval);
      }
    } catch (err: any) {
      console.error('Error generating QR code:', err);
      setError(err.response?.data?.message || 'Failed to generate QR code');
    }
  };

  // Timer countdown
  useEffect(() => {
    if (timeRemaining > 0) {
      const timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeRemaining === 0 && event && qrCodeUrl) {
      // Auto refresh QR code when timer reaches 0
      generateQRCode();
    }
  }, [timeRemaining, event, qrCodeUrl]);

  // Initial load
  useEffect(() => {
    if (eventId) {
      fetchEvent();
    }
  }, [eventId]);

  // Generate initial QR code when event is loaded
  useEffect(() => {
    if (event && !qrCodeUrl) {
      generateQRCode();
    }
  }, [event]);

  const handleBack = () => {
    navigate('/events');
  };

  const handleRefresh = () => {
    generateQRCode();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'orange';
      case 'ended':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Tạm dừng';
      case 'ended':
        return 'Đã kết thúc';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div>
        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Quay lại
          </Button>
        </Space>
        <Alert
          message="Lỗi"
          description={error || 'Không tìm thấy sự kiện'}
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Quay lại
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            Làm mới QR
          </Button>
        </Space>
        
        <Title level={2}>
          <QrcodeOutlined style={{ marginRight: 8 }} />
          QR Display - {event.eventName}
        </Title>
      </div>

      <Row gutter={24}>
        {/* Event Info */}
        <Col xs={24} lg={8}>
          <Card title="Thông tin sự kiện" style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong>Tên sự kiện:</Text>
                <br />
                <Text>{event.eventName}</Text>
              </div>
              
              <div>
                <Text strong>Địa điểm:</Text>
                <br />
                <Text>{event.location}</Text>
              </div>
              
              <div>
                <Text strong>Trạng thái:</Text>
                <br />
                <Tag color={getStatusColor(event.status)}>
                  {getStatusText(event.status)}
                </Tag>
              </div>
              
              <div>
                <Text strong>Thời gian:</Text>
                <br />
                <Text>
                  {new Date(event.startTime).toLocaleString('vi-VN')}
                  <br />
                  đến {new Date(event.endTime).toLocaleString('vi-VN')}
                </Text>
              </div>
              
              <div>
                <Text strong>Chu kỳ cập nhật QR:</Text>
                <br />
                <Text>{event.qrRefreshInterval} giây</Text>
              </div>
              
              {event.description && (
                <div>
                  <Text strong>Mô tả:</Text>
                  <br />
                  <Text>{event.description}</Text>
                </div>
              )}
            </Space>
          </Card>
        </Col>

        {/* QR Code Display */}
        <Col xs={24} lg={16}>
          <Card 
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>QR Code Tham gia sự kiện</span>
                <Space>
                  <Text>Làm mới sau: {timeRemaining}s</Text>
                  <Button 
                    type="primary" 
                    icon={<ReloadOutlined />} 
                    onClick={handleRefresh}
                    size="small"
                  >
                    Làm mới ngay
                  </Button>
                </Space>
              </div>
            }
            style={{ textAlign: 'center' }}
          >
            {qrCodeUrl ? (
              <div>
                <div style={{ 
                  display: 'inline-block', 
                  padding: '20px', 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <img 
                    src={qrCodeUrl} 
                    alt="QR Code" 
                    style={{ 
                      maxWidth: '400px', 
                      width: '100%',
                      height: 'auto'
                    }} 
                  />
                </div>
                
                <div style={{ marginTop: 16 }}>
                  <Text type="secondary">
                    Quét mã QR này để tham gia sự kiện hoặc mở bằng camera điện thoại
                  </Text>
                  <br />
                  <Text code style={{ fontSize: '12px', wordBreak: 'break-all' }}>
                    URL: {API_CONFIG.clientBaseURL}/join-event/{currentToken && typeof currentToken === 'string' ? currentToken.substring(0, 15) + '...' : 'Đang tạo...'}
                  </Text>
                </div>
                
                <Alert
                  message="Hướng dẫn sử dụng"
                  description={
                    <div>
                      <p>• Người dùng có thể quét QR bằng ứng dụng camera thông thường của điện thoại</p>
                      <p>• Hoặc mở ứng dụng game và sử dụng chức năng quét QR trong app</p>
                      <p>• QR code sẽ tự động làm mới sau {event.qrRefreshInterval} giây để đảm bảo bảo mật</p>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginTop: 16, textAlign: 'left' }}
                />
              </div>
            ) : (
              <div style={{ padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text>Đang tạo QR code...</Text>
                </div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default QRDisplayPage;
